import { PapelUsuario, UsuarioDetalhado, UsuarioListaItem } from '../api/user/userApi';
import { VarianteChip } from '../ui';

/**
 * Regras de papel e situação da central de usuários — funções PURAS (at-68).
 *
 * Existem porque a mesma decisão estava escrita em três lugares e já tinha divergido:
 * `usuarios.tsx` oferecia ADMIN só a um ADMIN, `criarUsuario.tsx` oferecia a todo mundo,
 * e o backend recusava os dois com 403. Aqui é um lugar só, e a tela renderiza
 * (convenção at-24/at-56).
 *
 * 📌 Quem decide de verdade é o backend. Isto evita oferecer o que será recusado e
 * permite DIZER o motivo na tela, em vez de esperar o 403 no clique.
 */

export interface OpcaoPapel {
  valor: PapelUsuario;
  rotulo: string;
  descricao: string;
}

/**
 * Os papéis do sistema. ⚠️ Lista em UM lugar: quando o 4º papel entrar (o `CADASTRO`,
 * de `PLANS/plan-0-usuario-cadastro-contexto.md`), ele entra aqui como DADO — a tela e
 * o formulário não mudam.
 */
export const PAPEIS: OpcaoPapel[] = [
  {
    valor: 'CONSULTOR',
    rotulo: 'Consultor',
    descricao: 'Cadastra associados, cotações, vistorias e termos',
  },
  {
    valor: 'TI',
    rotulo: 'TI',
    descricao: 'Administra o sistema, usuários e sincronizações',
  },
  {
    valor: 'ADMIN',
    rotulo: 'Administrador',
    descricao: 'Tudo o que o TI faz, mais as ações exclusivas de administrador',
  },
];

export const rotuloPapel = (papel?: string | null): string =>
  PAPEIS.find(p => p.valor === papel)?.rotulo ?? papel ?? '—';

/** ADMIN em destaque; os demais em neutro. Papel não é estado — não usa ok/pendente. */
export const varianteDoPapel = (papel?: string | null): VarianteChip =>
  papel === 'ADMIN' ? 'marca' : 'neutro';

/**
 * Papéis que este requisitante pode ATRIBUIR.
 *
 * Só um ADMIN define ADMIN. A exceção é o alvo que já É ADMIN: a opção aparece para o
 * select não mostrar um papel diferente do que o usuário tem hoje (o formulário estaria
 * mentindo sobre o cadastro). Nesse caso o TI não consegue salvar de qualquer forma —
 * `podeEditar` já o bloqueia antes.
 */
export function papeisOferecidos(
  requesterUserType?: string | null,
  papelDoAlvo?: string | null,
): OpcaoPapel[] {
  if (requesterUserType === 'ADMIN' || papelDoAlvo === 'ADMIN') return PAPEIS;

  return PAPEIS.filter(p => p.valor !== 'ADMIN');
}

/** TI não edita um ADMIN (regra do backend desde a at-11). */
export function podeEditar(
  requesterUserType?: string | null,
  papelDoAlvo?: string | null,
): boolean {
  if (papelDoAlvo === 'ADMIN') return requesterUserType === 'ADMIN';

  return requesterUserType === 'TI' || requesterUserType === 'ADMIN';
}

/** Motivo da trava, em texto — a tela precisa DIZER, não só desabilitar. */
export function motivoSemEdicao(
  requesterUserType?: string | null,
  papelDoAlvo?: string | null,
): string | null {
  if (podeEditar(requesterUserType, papelDoAlvo)) return null;

  if (papelDoAlvo === 'ADMIN') {
    return 'Apenas um administrador pode editar outro administrador.';
  }

  return 'Apenas TI ou administrador pode editar usuários.';
}

/**
 * Quem pode ativar/desativar. Além da regra de papel, **ninguém desativa a si mesmo** —
 * sem essa guarda o único TI de plantão se tranca fora, e não há tela para desfazer.
 */
export function podeAlterarSituacao(
  requesterUserType: string | null | undefined,
  papelDoAlvo: string | null | undefined,
  requesterId: string | null | undefined,
  idDoAlvo: string,
): boolean {
  if (requesterId && requesterId === idDoAlvo) return false;

  return podeEditar(requesterUserType, papelDoAlvo);
}

export function motivoSemAlterarSituacao(
  requesterUserType: string | null | undefined,
  papelDoAlvo: string | null | undefined,
  requesterId: string | null | undefined,
  idDoAlvo: string,
): string | null {
  if (podeAlterarSituacao(requesterUserType, papelDoAlvo, requesterId, idDoAlvo)) return null;

  if (requesterId && requesterId === idDoAlvo) {
    return 'Você não pode desativar a sua própria conta.';
  }

  if (papelDoAlvo === 'ADMIN') {
    return 'Apenas um administrador pode desativar outro administrador.';
  }

  return 'Apenas TI ou administrador pode alterar a situação de um usuário.';
}

/**
 * 🔴 Só CONSULTOR tem código de voluntário (decisão de Victor, 04/11/2026 — at-87). É a chave
 * da carteira; TI e ADMIN não têm carteira, então não têm código — o campo nem aparece para eles.
 * O backend aplica a mesma regra (400 nos dois sentidos).
 */
export function volunteerCodeObrigatorio(papel: PapelUsuario): boolean {
  return papel === 'CONSULTOR';
}

export interface RotuloSituacao {
  texto: string;
  variante: VarianteChip;
}

type UsuarioParaSituacao = Pick<UsuarioListaItem | UsuarioDetalhado, 'ativo' | 'firstLogin'>;

/**
 * Situação do acesso, em TEXTO e cor (spec 02 §9 — nunca só cor).
 *
 * ⚠️ "Primeiro acesso pendente" é ÂMBAR, não vermelho: é um estado normal de quem acabou
 * de ser cadastrado, não uma falha (spec 02 §2.3). E "Inativo" é neutro — foi uma decisão
 * do TI, não um problema do sistema.
 */
export function rotuloSituacao(usuario: UsuarioParaSituacao): RotuloSituacao {
  if (!usuario.ativo) return { texto: 'Inativo', variante: 'neutro' };
  if (usuario.firstLogin) return { texto: 'Primeiro acesso pendente', variante: 'pendente' };

  return { texto: 'Ativo', variante: 'ok' };
}
